// ── Nota de voz ──────────────────────────────────────────────────────────────
// Una nota de voz de WhatsApp dentro de la bitácora del contacto.
//
// No usa `<audio controls>`: el control nativo lo dibuja el navegador con su
// propio gris, su propia tipografía y su propio alto, y en una bandeja oscura se
// lee como un pedazo de otra aplicación pegado adentro. Acá el reproductor es
// del sistema —IconButton, tokens de color y de espaciado— y el `<audio>` va
// escondido haciendo el trabajo.
//
// API cerrada a propósito (REGLA #16): ni className ni style. Lo único que
// entra es de dónde sale el sonido y cuánto pesa.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { IconButton } from './IconButton.js';

export interface VoiceNoteProps {
  /** URL del archivo. null mientras todavía no se bajó. */
  url: string | null;
  /** Por qué no está, cuando el archivo no se pudo traer. */
  error?: string | null;
}

function reloj(segundos: number): string {
  if (!Number.isFinite(segundos) || segundos < 0) return '0:00';
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function VoiceNote({ url, error = null }: VoiceNoteProps) {
  const { t } = useTranslation();
  const audio = useRef<HTMLAudioElement | null>(null);
  const [sonando, setSonando] = useState(false);
  const [posicion, setPosicion] = useState(0);
  const [duracion, setDuracion] = useState(0);

  useEffect(() => {
    // Cambió el archivo: lo que estaba sonando ya no es esto.
    setSonando(false);
    setPosicion(0);
    setDuracion(0);
  }, [url]);

  if (!url) {
    return (
      <div style={contenedor}>
        <span style={{ color: 'var(--evo-fg-3)', display: 'inline-flex' }}>
          <Icon name={error ? 'alert' : 'clock'} size={14} />
        </span>
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
          {error ? t('messages.voiceNoteFailed') : t('messages.voiceNoteDownloading')}
        </span>
      </div>
    );
  }

  const avance = duracion > 0 ? Math.min(posicion / duracion, 1) : 0;

  function alternar() {
    const el = audio.current;
    if (!el) return;
    if (el.paused) { void el.play(); } else { el.pause(); }
  }

  return (
    <div style={contenedor}>
      <IconButton
        icon={<Icon name={sonando ? 'pause' : 'play'} size={14} />}
        ariaLabel={sonando ? t('messages.voiceNotePause') : t('messages.voiceNotePlay')}
        size="sm"
        onClick={alternar}
      />

      {/* La barra es de lectura: mueve el tiempo, no se arrastra. Una nota de
          voz dura segundos — buscar dentro de ella es una precisión que nadie
          necesita y un control más que mantener. */}
      <div style={{
        flex: 1, minWidth: 'var(--evo-voice-min-w)', height: 'var(--evo-voice-bar-h)', borderRadius: 'var(--evo-r-pill)',
        background: 'var(--evo-stroke)', overflow: 'hidden',
      }}>
        <div style={{
          width: `${avance * 100}%`, height: '100%',
          background: 'var(--evo-ch-whatsapp)',
        }} />
      </div>

      <span style={{
        fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)',
        fontVariantNumeric: 'tabular-nums', flexShrink: 0,
      }}>
        {reloj(sonando || posicion > 0 ? posicion : duracion)}
      </span>

      <audio
        ref={audio}
        src={url}
        preload="metadata"
        onPlay={() => setSonando(true)}
        onPause={() => setSonando(false)}
        onEnded={() => { setSonando(false); setPosicion(0); }}
        onTimeUpdate={e => setPosicion(e.currentTarget.currentTime)}
        onLoadedMetadata={e => setDuracion(e.currentTarget.duration)}
        style={{ display: 'none' }}
      />
    </div>
  );
}

const contenedor = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--evo-space-3)',
  padding: 'var(--evo-space-2) var(--evo-space-3)',
  background: 'var(--evo-bg-2)',
  borderRadius: 'var(--evo-r)',
  maxWidth: 'var(--evo-voice-max-w)',
} as const;
